// `/s` AI query understanding (P3D, docs/search-v2-s-autocomplete-ai.md): the deterministic half,
// shared by the browser and the server.
//
// - `shouldInterpretQuery`: the gate. Only a manual `/s` submit whose text has an explicit price,
//   condition or delivery cue is sent to `POST /api/search/interpret`; the server re-runs it.
// - `findCues` / `findSubcategoryMatches`: the word spans the server validator requires before it
//   accepts a field the model returned (server/search-ai/interpret.js).
// - `interpretedSearchParams` / `withAiAttribution` / `exactSearchParams`: interpretation ↔ `/s`
//   URL parameters; `ai_a` records which filters the interpretation actually changed.
//
// Pure ES module without imports: the server loads it through require(esm) like the Search V2
// vocabulary modules, so it cannot import JSON (the taxonomy is passed in).

export const INTERPRET_MAX_QUERY_CHARS = 100;
export const AI_QUERY_PARAM = 'ai_q';

// Same canonical text normalization as `normText` in server/search-v2/normalize.js.
export const normInterpretText = s =>
  String(s == null ? '' : s)
    .normalize('NFKC')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ');

// Edge punctuation is ignored for matching; `$` may lead and `#`/`%` may trail (price and unit).
const tokenKey = raw => raw.replace(/^[^a-z0-9À-￿$#]+/, '').replace(/[^a-z0-9À-￿%#]+$/, '');

/** Normalized text → tokens `{ raw, key }` (raw = the normalized word as typed). */
export const tokenizeInterpretQuery = text => {
  const n = normInterpretText(text);
  return n ? n.split(' ').map(raw => ({ raw, key: tokenKey(raw) })) : [];
};

const singular = w =>
  w.length > 3 && w.endsWith('ies')
    ? `${w.slice(0, -3)}y`
    : w.length > 3 && /(ses|xes|ches|shes)$/.test(w)
    ? w.slice(0, -2)
    : w.length > 2 && w.endsWith('s') && !w.endsWith('ss')
    ? w.slice(0, -1)
    : w;

// ---- price -------------------------------------------------------------------------------------

export const PRICE_MIN_USD = 1;
export const PRICE_MAX_USD = 100000;

// Upper-bound cues (P3D v1). Lower-bound language (over, above, more than, at least, X or more)
// is deliberately not a cue: those words stay in the query.
const UPPER_CUES = [
  ['under'],
  ['below'],
  ['less', 'than'],
  ['max'],
  ['maximum'],
  ['up', 'to'],
  ['budget', 'of'],
  ['budget', 'is'],
  ['budget'],
  ['cheaper', 'than'],
];
const CURRENCY_WORDS = ['usd', 'dollar', 'dollars', 'buck', 'bucks'];
// A number followed by one of these is a measurement, not a price.
const UNIT_WORDS = [
  'grain',
  'grains',
  'gr',
  'lb',
  'lbs',
  'pound',
  'pounds',
  '#',
  'in',
  'inch',
  'inches',
  'ft',
  'foot',
  'feet',
  'cm',
  'centimeter',
  'centimeters',
  'mm',
  'millimeter',
  'millimeters',
  'yd',
  'yds',
  'yard',
  'yards',
  '%',
];
// Inch / foot marks: ASCII, typographic quotes and primes (NFKC turns ″ into ′′). Edge punctuation
// is stripped from the token key, so they are read from the raw token.
const MEASURE_MARK = /["'′″”’]/;
const isMarkOnly = raw => /^["'′″”’]+[.,;:!?)]*$/.test(raw);
const AMOUNT = /^\$?(\d{1,3}(?:,\d{3})+|\d+)(?:\.(\d{1,2}))?(k)?$/;

/** Token key → dollar amount, or null when the token is not a bare amount (`rx1400i`, `70lb`). */
const parseAmount = key => {
  const m = AMOUNT.exec(key);
  if (!m) return null;
  const n = Number(`${m[1].replace(/,/g, '')}${m[2] ? `.${m[2]}` : ''}`);
  const value = m[3] ? n * 1000 : n;
  return Number.isFinite(value) ? value : null;
};

const isUnitAt = (tokens, i) => {
  const t = tokens[i];
  if (!t) return false;
  // "in store" / "in stock" are words, not inches
  if (t.key === 'in') return !['store', 'stock'].includes(tokens[i + 1]?.key);
  return UNIT_WORDS.includes(t.key) || isMarkOnly(t.raw);
};

const matchSeq = (tokens, i, seq) => seq.every((w, j) => tokens[i + j]?.key === w);

/**
 * An amount at token i (optionally after a lone `$`). Returns `{ value, end }` (end exclusive,
 * including a trailing currency word) or null when it is not a price (unit, identifier, range).
 */
const amountAt = (tokens, i) => {
  let j = i;
  if (tokens[j]?.raw === '$') j += 1;
  const t = tokens[j];
  if (!t) return null;
  const value = parseAmount(t.key);
  if (value == null) return null;
  // the amount itself carries an inch/foot mark (30", 30″, 6'): read before the key drops it
  if (MEASURE_MARK.test(t.raw.slice(t.raw.search(/\d/)))) return null;
  const hasCurrency = t.key.startsWith('$') || j > i || CURRENCY_WORDS.includes(tokens[j + 1]?.key);
  if (!hasCurrency && isUnitAt(tokens, j + 1)) return null;
  const end = CURRENCY_WORDS.includes(tokens[j + 1]?.key) ? j + 2 : j + 1;
  return { value, end };
};

const inPriceRange = v => Number.isInteger(v) && v >= PRICE_MIN_USD && v <= PRICE_MAX_USD;

// ---- condition / delivery ----------------------------------------------------------------------

// The listing condition values of the sidebar (`New | Used | Open Box`). A bare "new" is not a cue:
// "new mathews lift" usually means a recent model.
export const CONDITION_CUES = {
  Used: [
    ['used'],
    ['pre-owned'],
    ['preowned'],
    ['pre', 'owned'],
    ['second', 'hand'],
    ['secondhand'],
    ['second-hand'],
  ],
  'Open Box': [['open', 'box'], ['open-box'], ['openbox'], ['like', 'new'], ['like-new']],
  New: [['brand', 'new'], ['brand-new'], ['new', 'in', 'box'], ['nib'], ['unused']],
};
export const DELIVERY_CUES = {
  shipping: [['free', 'shipping'], ['ship'], ['ships'], ['shipped'], ['shipping']],
  pickup: [
    ['local', 'pickup'],
    ['pickup'],
    ['pick', 'up'],
    ['pick-up'],
    ['in', 'store'],
    ['in-store'],
    ['instore'],
  ],
};

const findWordCues = (tokens, cueMap) => {
  const found = [];
  for (let i = 0; i < tokens.length; i += 1) {
    let best = null;
    Object.keys(cueMap).forEach(value =>
      cueMap[value].forEach(seq => {
        if (matchSeq(tokens, i, seq) && (!best || seq.length > best.end - best.start)) {
          best = { value, start: i, end: i + seq.length };
        }
      })
    );
    if (best) {
      found.push(best);
      i = best.end - 1;
    }
  }
  return found;
};

/**
 * Every supported cue in the tokens, as spans `{ start, end }` (end exclusive):
 * `price`: `{ max }` (upper bound) or `{ min, max }` (between X and Y), whole-dollar amounts within
 * 1–100000 only; `condition` / `delivery`: `{ value }` (canonical filter value).
 */
export const findCues = tokens => {
  const price = [];
  for (let i = 0; i < tokens.length; i += 1) {
    if (tokens[i].key === 'between') {
      const a = amountAt(tokens, i + 1);
      if (a && ['and', 'to', '-'].includes(tokens[a.end]?.key)) {
        const b = amountAt(tokens, a.end + 1);
        if (b && inPriceRange(a.value) && inPriceRange(b.value) && a.value < b.value) {
          price.push({ min: a.value, max: b.value, start: i, end: b.end });
          i = b.end - 1;
        }
      }
      continue;
    }
    const cue = UPPER_CUES.find(seq => matchSeq(tokens, i, seq));
    if (cue) {
      const a = amountAt(tokens, i + cue.length);
      if (a && inPriceRange(a.value)) {
        price.push({ max: a.value, start: i, end: a.end });
        i = a.end - 1;
      }
    }
  }
  return {
    price,
    condition: findWordCues(tokens, CONDITION_CUES),
    delivery: findWordCues(tokens, DELIVERY_CUES),
  };
};

/**
 * The deterministic AI gate (browser and server): ≤ 100 normalized characters, ≥ 2 words and at
 * least one price (upper bound / between), condition or delivery cue. Category wording alone never
 * qualifies.
 */
export const shouldInterpretQuery = text => {
  const n = normInterpretText(text);
  if (!n || n.length > INTERPRET_MAX_QUERY_CHARS) return false;
  const tokens = tokenizeInterpretQuery(n);
  if (tokens.length < 2) return false;
  const cues = findCues(tokens);
  return cues.price.length > 0 || cues.condition.length > 0 || cues.delivery.length > 0;
};

// ---- subcategory wording -----------------------------------------------------------------------

const labelPhrases = label =>
  String(label)
    .toLowerCase()
    .split(/[&,]/)
    .map(p =>
      p
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .map(singular)
    )
    .filter(words => words.length > 0);

/**
 * Subcategories whose label wording occurs in the tokens (a label phrase split on `&`/`,`, singular
 * and plural alike): `{ option, category, start, end }`. A match inside a longer match of another
 * subcategory is left out ("arrow" of Arrows & Shafts inside "arrow rests").
 */
export const findSubcategoryMatches = (tokens, subcategories) => {
  const keys = tokens.map(t => singular(t.key));
  const matches = [];
  (subcategories || []).forEach(sub =>
    labelPhrases(sub.label).forEach(words => {
      for (let i = 0; i + words.length <= keys.length; i += 1) {
        if (words.every((w, j) => keys[i + j] === w)) {
          matches.push({
            option: sub.option,
            category: sub.category,
            start: i,
            end: i + words.length,
          });
        }
      }
    })
  );
  return matches.filter(
    m =>
      !matches.some(
        o =>
          o.option !== m.option &&
          o.start <= m.start &&
          o.end >= m.end &&
          o.end - o.start > m.end - m.start
      )
  );
};

// ---- URL translation ---------------------------------------------------------------------------

const withoutEmpty = params =>
  Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ''));

/**
 * Validated interpretation (server response) + current `/s` params → the params of the one
 * canonical navigation. Each accepted field replaces the same URL filter (the submitted text is the
 * newest instruction); unrelated params (brand, channel, iframe, …) are kept. `ai_q` keeps the
 * original text.
 */
export const interpretedSearchParams = (params, interpretation, originalQuery) => {
  const next = { ...params, keywords: interpretation.query || null };
  const sub = interpretation.subcategory || [];
  if (sub.length === 1 && (interpretation.category || []).length === 1) {
    next.pub_category = interpretation.category[0];
    next.pub_subcategory = sub[0];
  }
  if ((interpretation.condition || []).length) {
    next.pub_condition = interpretation.condition.join(',');
  }
  if ((interpretation.delivery || []).length) {
    next.pub_deliveryOption = `has_any:${interpretation.delivery.join(',')}`;
  }
  if (Number.isInteger(interpretation.price_max_usd)) {
    const min = Number.isInteger(interpretation.price_min_usd) ? interpretation.price_min_usd : 0;
    next.price = `${min},${interpretation.price_max_usd}`;
  }
  next[AI_QUERY_PARAM] = String(originalQuery)
    .trim()
    .replace(/\s+/g, ' ')
    .slice(0, INTERPRET_MAX_QUERY_CHARS);
  return withoutEmpty(next);
};

// ---- AI attribution (`ai_a`) -------------------------------------------------------------------

export const AI_ATTRIBUTION_PARAM = 'ai_a';
const AI_ATTRIBUTION_VERSION = 1;
// The URL filters an interpretation can set, as logical fields: category and subcategory are one
// coupled taxonomy field, price is one "min,max" param.
const ATTRIBUTION_GROUPS = [
  ['pub_category', 'pub_subcategory'],
  ['pub_condition'],
  ['pub_deliveryOption'],
  ['price'],
];
const ATTRIBUTED_PARAMS = [].concat(...ATTRIBUTION_GROUPS);
const urlValue = v => (v == null || v === '' ? null : v);
const sameValue = (a, b) => JSON.stringify(urlValue(a)) === JSON.stringify(urlValue(b));
const isUrlValue = v =>
  v === null || typeof v === 'string' || (Array.isArray(v) && v.every(x => typeof x === 'string'));

/**
 * Canonical `/s` params before and after an interpretation → the same `after` plus `ai_a`: the
 * filter params the interpretation actually changed, `[param, applied, previous]` (null = absent),
 * as versioned JSON. Presentation/history state only: the Search V2 adapter never reads it.
 */
export const withAiAttribution = (before, after) => {
  const changes = ATTRIBUTED_PARAMS.filter(p => !sameValue(before[p], after[p])).map(p => [
    p,
    urlValue(after[p]),
    urlValue(before[p]),
  ]);
  const next = { ...after };
  delete next[AI_ATTRIBUTION_PARAM];
  if (changes.length) {
    next[AI_ATTRIBUTION_PARAM] = JSON.stringify({ v: AI_ATTRIBUTION_VERSION, c: changes });
  }
  return next;
};

/** `ai_a` value → `[param, applied, previous]` entries; anything malformed → [] (no attribution). */
export const parseAiAttribution = raw => {
  if (typeof raw !== 'string') return [];
  try {
    const o = JSON.parse(raw);
    const ok =
      o &&
      o.v === AI_ATTRIBUTION_VERSION &&
      Array.isArray(o.c) &&
      o.c.every(
        e =>
          Array.isArray(e) &&
          e.length === 3 &&
          ATTRIBUTED_PARAMS.includes(e[0]) &&
          isUrlValue(e[1]) &&
          isUrlValue(e[2])
      );
    return ok ? o.c : [];
  } catch (e) {
    return [];
  }
};

/**
 * "Search exactly for this text": keywords = `ai_q`; `ai_q` and `ai_a` removed; every filter the
 * interpretation applied (per `ai_a`) and the user has not changed since goes back to its
 * pre-interpretation value (or is removed when there was none). A logical field the user changed
 * afterwards, and every filter the AI did not apply, are kept. Never calls the AI.
 */
export const exactSearchParams = params => {
  const text = typeof params[AI_QUERY_PARAM] === 'string' ? params[AI_QUERY_PARAM] : '';
  const attribution = parseAiAttribution(params[AI_ATTRIBUTION_PARAM]);
  const next = { ...params, keywords: text.slice(0, INTERPRET_MAX_QUERY_CHARS) || null };
  delete next[AI_QUERY_PARAM];
  delete next[AI_ATTRIBUTION_PARAM];
  ATTRIBUTION_GROUPS.forEach(group => {
    const entries = attribution.filter(e => group.includes(e[0]));
    if (!entries.length || !entries.every(([p, applied]) => sameValue(params[p], applied))) return;
    entries.forEach(([p, , previous]) => {
      next[p] = previous;
    });
  });
  return withoutEmpty(next);
};
